import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Globe, Lock } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useSettings, mediaUrl } from "@/context/SettingsContext";

const Navbar = () => {
  const { lang, toggle, t } = useLanguage();
  const { settings } = useSettings();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const links = [
    { to: "/", label: t.nav.home, testid: "navbar-link-accueil" },
    { to: "/services", label: t.nav.services, testid: "navbar-link-services" },
    { to: "/histoire", label: t.nav.history, testid: "navbar-link-histoire" },
    { to: "/galerie", label: t.nav.gallery, testid: "navbar-link-galerie" },
    { to: "/vehicules", label: t.vehicles.nav, testid: "navbar-link-vehicules" },
    { to: "/contact", label: t.nav.contact, testid: "navbar-link-contact" },
    { to: "/actualites", label: t.nav.news, testid: "navbar-link-actualites" },
    { to: "/recrutement", label: t.recruit.nav, testid: "navbar-link-recrutement" },
    { to: "/suivi", label: t.nav.track, testid: "navbar-link-suivi" },
    { to: "/admin", label: t.nav.login, testid: "navbar-link-connexion", icon: Lock },
  ].filter((l) => !(l.to === "/suivi" && settings.sections?.nav_track === false) && !(l.to === "/admin" && settings.sections?.nav_login === false) && !(l.to === "/actualites" && settings.sections?.nav_news === false) && !(l.to === "/histoire" && settings.sections?.history === false) && !(l.to === "/recrutement" && settings.sections?.recruit === false) && !(l.to === "/vehicules" && settings.sections?.nav_vehicles === false));

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-brand/10 bg-white/85 backdrop-blur-xl">
      <div className="h-1 w-full bg-gradient-to-r from-brand via-brand to-gold" />
      <div className="mx-auto flex max-w-[1440px] items-center justify-between gap-6 px-5 py-3 sm:px-8">
        <Link to="/" data-testid="navbar-logo-link" className="group flex shrink-0 flex-col items-center">
          <img
            src={mediaUrl(settings.logo_url)}
            alt={settings.company_name}
            className="h-10 w-auto transition-transform duration-500 group-hover:scale-105 sm:h-11"
            data-testid="navbar-logo-image"
          />
          <span className="font-display mt-0.5 block whitespace-nowrap text-[11px] font-extrabold uppercase italic tracking-wide text-gold sm:text-xs" data-testid="navbar-logo-name">
            {settings.company_name}
          </span>
        </Link>

        <nav className="hidden items-center gap-5 lg:flex xl:gap-7">
          {links.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={link.testid}
              className={({ isActive }) =>
                `relative whitespace-nowrap font-mono text-[11px] uppercase tracking-[0.18em] transition-colors duration-300 after:absolute after:-bottom-1.5 after:left-0 after:h-0.5 after:bg-gold after:transition-all after:duration-300 ${
                  isActive ? "text-gold after:w-full" : "text-forest after:w-0 hover:text-brand hover:after:w-full"
                }`
              }
            >
              <span className="inline-flex items-center gap-1.5">{link.icon && <link.icon className="h-3 w-3" />}{link.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          <button
            onClick={toggle}
            data-testid="language-switcher-button"
            className="flex items-center gap-2 rounded-full border border-brand/30 px-3 py-2 font-mono text-xs uppercase tracking-widest text-brand transition-colors duration-300 hover:border-brand hover:bg-mint"
          >
            <Globe className="h-3.5 w-3.5" />
            {lang === "fr" ? "EN" : "FR"}
          </button>
          <Link
            to="/contact?type=devis#formulaire"
            data-testid="navbar-devis-cta-button"
            className="whitespace-nowrap rounded-full bg-gold px-5 py-2.5 font-mono text-xs font-semibold uppercase tracking-widest text-white shadow-lg shadow-gold/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold-hover"
          >
            {t.nav.cta}
          </Link>
        </div>

        <div className="flex items-center gap-3 lg:hidden">
          <button
            onClick={toggle}
            data-testid="language-switcher-button-mobile"
            className="rounded-full border border-brand/30 px-2.5 py-2 font-mono text-xs uppercase text-brand"
          >
            {lang === "fr" ? "EN" : "FR"}
          </button>
          <button
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            className="text-brand"
            aria-label="Menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-brand/10 bg-white lg:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {links.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  onClick={() => setOpen(false)}
                  data-testid={`${link.testid}-mobile`}
                  className={
                    location.pathname === link.to
                      ? "py-3 font-display text-lg font-bold text-gold"
                      : "py-3 font-display text-lg font-bold text-forest"
                  }
                >
                  {link.label}
                </NavLink>
              ))}
              <Link
                to="/contact?type=devis#formulaire"
                onClick={() => setOpen(false)}
                data-testid="navbar-devis-cta-button-mobile"
                className="mt-2 rounded-full bg-gold px-5 py-3 text-center font-mono text-xs font-semibold uppercase tracking-widest text-white"
              >
                {t.nav.cta}
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
